import { GAME_ORIGIN } from "../lib/siteConfig";
import { getChars, prefetchChars } from "../lib/charsCache";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Newspaper, Trophy, Crown, Shield, ArrowRight, Heart, Share2, MessageCircle, MoreHorizontal, Image as ImageIcon, X, Send, Trash2, LayoutGrid, Loader2, Wifi, WifiOff, Star, Facebook, Twitter, Link as LinkIcon, Play } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { toast } from "sonner";
import { HomeHero, HomeFeatures, HomeJohto, HomeBirds, NewsHeader } from "../components/home/HomeSections";

export default function Home() {
  const { account } = useAuth();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { isConnected: isWsConnected, subscribe } = useWebSocket();
  const isRed = theme === 'red';
  const fileInputRef = useRef(null);

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Admin States (Regra Flexível: ID 1 ou qualquer variação de 'admin')
  const isAdmin = account && (
    String(account.id) === "1" || 
    (account.role && account.role.toLowerCase() === "admin")
  );
  const [postData, setPostData] = useState({ title: "", content: "", category: "Notícia", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [sharingPost, setSharingPost] = useState(null);
  const [loadingPlay, setLoadingPlay] = useState(false);
  const navigate = useNavigate();

  const handlePlayGame = async () => {
    if (!account) {
      toast.error("Você precisa estar logado na sua conta para jogar PKX_IDLE!");
      navigate("/login");
      return;
    }
    setLoadingPlay(true);
    try {
      const characters = await getChars();
      if (characters.length === 0) {
        toast.info("Você precisa criar um personagem antes de entrar no jogo!");
        navigate("/panel");
        return;
      }
      if (characters.length === 1) {
        const char = characters[0];
        const ticketRes = await api.post("/game/launch-ticket", { character_id: char.id });
        if (ticketRes.data?.success && ticketRes.data?.ticket) {
          toast.success(`Iniciando PKX_IDLE com ${char.name}...`);
          const gameUrl = `${GAME_ORIGIN}/?ticket=${encodeURIComponent(ticketRes.data.ticket)}&lang=${encodeURIComponent(localStorage.getItem("lang") || "pt")}`;
          { const __u = gameUrl; const __w = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ? null : window.open(__u, "_blank"); if (!__w) window.location.href = __u; }
          return;
        }
      }
      toast.info("Selecione seu personagem para jogar!");
      navigate("/panel");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao conectar com o jogo.");
      navigate("/panel");
    } finally {
      setLoadingPlay(false);
    }
  };

  const loadNews = useCallback(async () => {
    try {
      const r = await api.get("/news");
      setNews(r.data.data || []);
    } catch (err) {
      console.warn("[HOME] Falha ao carregar notícias");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Pkxidle";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "PKX Idle: um MMORPG de Pokémon no navegador, com caça automática, economia entre jogadores, rankings e Wiki.");
    }
    
    loadNews();

    // Inscrição para atualizações em tempo real
    const unsubscribe = subscribe((data) => {
      if (data.type === "new_post" || data.type === "news_updated") {
        loadNews();
        if (data.type === "new_post") {
          toast.info("Novo conteúdo publicado no feed!", { duration: 2000 });
        }
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [loadNews, subscribe]);

  const fetchGallery = async () => {
    try {
      const res = await api.get("/admin/gallery");
      // Standardized to {success, data}
      setGalleryImages(res.data.data || []);
    } catch (err) {
      setGalleryImages([]);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const uploadRes = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const imageUrl = uploadRes.data.url || uploadRes.data.imageUrl;
      if (imageUrl) {
        setPostData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success("Imagem sincronizada com sucesso!");
      } else {
        toast.error("Servidor não retornou um link de imagem válido.");
      }
    } catch (err) {
      toast.error("Erro no serviço de imagem");
    } finally {
      setUploading(false);
    }
  };

  const handlePublish = async () => {
    if (!postData.title || !postData.content) return toast.error("Preencha todos os campos!");
    setPublishing(true);
    try {
      await api.post("/admin/news", postData);
      toast.success("Publicado com sucesso!");
      setPostData({ title: "", content: "", category: "Notícia", image_url: "" });
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const message = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) 
            ? errorDetail[0].msg 
            : (typeof errorDetail === 'object' && errorDetail !== null ? JSON.stringify(errorDetail) : "Erro ao publicar"));
      toast.error(message);
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteNews = async (id) => {
    if (!confirm("Excluir esta notícia?")) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success("Removido com sucesso!");
    } catch (err) {
      toast.error("Erro ao excluir");
    }
  };

  const getCategoryStyles = (cat) => {
    switch (cat?.toLowerCase()) {
      case 'evento': return { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', icon: <Star size={14} />, glow: 'shadow-purple-500/20' };
      case 'torneio': return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', icon: <Trophy size={14} />, glow: 'shadow-yellow-500/20' };
      default: return { 
        bg: isRed ? 'bg-red-500/20' : 'bg-blue-500/20', 
        text: isRed ? 'text-red-400' : 'text-blue-400', 
        border: isRed ? 'border-red-500/30' : 'border-blue-500/30', 
        icon: <Newspaper size={14} />, 
        glow: isRed ? 'shadow-red-500/20' : 'shadow-blue-500/20' 
      };
    }
  };

  const handleLike = async (id) => {
    try {
      await api.post(`/news/${id}/like`);
      // O WebSocket vai atualizar para todos, mas podemos ser rápidos aqui também
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao curtir";
      toast.error(msg);
    }
  };

  const handleShare = (n) => {
    setSharingPost(n);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copiado com sucesso!");
  };

  return (
    <div className="space-y-4 pb-10">
      
      {/* LIGHTBOX / VISUALIZADOR DE IMAGEM */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[500] bg-black/90 backdrop-blur-3xl flex items-center justify-center p-4 md:p-20 animate-in fade-in zoom-in-95 duration-300 cursor-pointer"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className={`absolute top-24 right-10 p-6 rounded-full transition-all z-[600] border backdrop-blur-3xl shadow-2xl active:scale-95 ${
              isRed 
                ? "bg-red-500/20 text-red-500 border-red-500/40 shadow-red-500/30 hover:bg-red-500/40" 
                : "bg-blue-500/20 text-blue-400 border-blue-500/40 shadow-blue-500/30 hover:bg-blue-500/40"
            }`}
            onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
          >
            <X size={40} strokeWidth={3} />
          </button>
          <img 
            src={selectedImage} 
            alt="Preview" 
            className="max-w-full max-h-full object-contain rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.8)] border border-white/10 shadow-[var(--color-main)]/20 cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      
      <HomeHero onPlay={handlePlayGame} loadingPlay={loadingPlay} account={account} />
      <HomeFeatures />
      <HomeJohto />
      <HomeBirds />
      <NewsHeader />

      {/* SOCIAL FEED NEWS SECTION */}
      <div className="max-w-[1100px] mx-auto px-6 py-10 space-y-12">
        
        {/* Admin Post Box */}
        {isAdmin && (
          <div className="bg-[#0c0d12]/60 backdrop-blur-3xl border border-white/10 rounded-[40px] p-10 space-y-8 animate-in slide-in-from-top-10 duration-500 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                <Crown className="text-blue-500" size={24} />
              </div>
              <input type="text" placeholder="Título da publicação..." className="flex-1 bg-white/5 border border-white/5 rounded-2xl px-6 py-3 text-white font-bold focus:border-blue-500/50 outline-none transition-all" value={postData.title} onChange={e => setPostData({...postData, title: e.target.value})} />
            </div>
            
            <textarea placeholder="O que há de novo, Administrador?" className="w-full bg-white/5 border border-white/5 rounded-3xl px-6 py-6 text-white min-h-[120px] focus:border-blue-500/50 outline-none transition-all resize-none font-medium" value={postData.content} onChange={e => setPostData({...postData, content: e.target.value})} />

            {postData.image_url && (
              <div className="relative rounded-[30px] overflow-hidden border border-white/10 aspect-video bg-black shadow-2xl">
                <img src={postData.image_url} alt="Preview" className="w-full h-full object-cover" />
                <button onClick={() => setPostData({...postData, image_url: ""})} className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-red-500 transition-all"><X size={16} /></button>
              </div>
            )}

            <div className="flex items-center justify-between pt-6 border-t border-white/5">
              <div className="flex items-center gap-3">
                <button onClick={() => fileInputRef.current.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
                  {uploading ? <Loader2 className="animate-spin text-blue-500" size={16} /> : <ImageIcon size={16} className="text-green-500" />}
                  {uploading ? "Subindo..." : "Anexar Foto"}
                </button>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                
                <button onClick={() => { setShowGallery(true); fetchGallery(); }} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 transition-all text-[10px] font-black uppercase tracking-widest">
                  <LayoutGrid size={16} className="text-purple-500" /> Galeria
                </button>

                <select 
                  className="bg-[#1a1c23] border border-white/10 text-white text-[10px] font-black uppercase tracking-widest rounded-xl px-4 py-2 outline-none focus:border-[var(--color-main)] cursor-pointer"
                  value={postData.category} 
                  onChange={e => setPostData({...postData, category: e.target.value})}
                >
                  <option value="Notícia" className="bg-[#1a1c23] text-white">Notícia</option>
                  <option value="Evento" className="bg-[#1a1c23] text-white">Evento</option>
                  <option value="Torneio" className="bg-[#1a1c23] text-white">Torneio</option>
                  <option value="Update" className="bg-[#1a1c23] text-white">Update</option>
                </select>
              </div>

              <button onClick={handlePublish} disabled={uploading || publishing} className="flex items-center gap-2 px-8 py-3 rounded-2xl bg-blue-600 text-white font-black uppercase tracking-widest text-xs hover:bg-blue-500 transition-all shadow-[0_10px_20px_rgba(37,99,235,0.3)] active:scale-95 disabled:opacity-50">
                {publishing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                {publishing ? "Enviando..." : "Publicar Feed"}
              </button>
            </div>
          </div>
        )}

        {/* Social Share Modal */}
        {sharingPost && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl z-[400] flex items-center justify-center p-6 animate-in fade-in duration-300">
             <div className="bg-[#0c0d12] border border-white/10 rounded-[40px] w-full max-w-md p-10 space-y-8 shadow-2xl relative">
                <button onClick={() => setSharingPost(null)} className="absolute top-6 right-6 p-2 bg-white/5 rounded-full hover:bg-white/10 text-white/40 hover:text-white transition-all"><X size={18}/></button>
                
                <div className="text-center space-y-2">
                   <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter">Compartilhar</h2>
                   <p className="text-[10px] text-white/20 uppercase font-bold tracking-[4px]">Espalhe a novidade pelo mundo</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                   <a 
                     href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/news/' + sharingPost.id)}`} 
                     target="_blank" rel="noreferrer"
                     className="flex flex-col items-center gap-3 p-6 bg-blue-600/10 border border-blue-600/20 rounded-3xl hover:bg-blue-600/20 transition-all group"
                   >
                      <Facebook size={32} className="text-blue-500 group-hover:scale-110 transition-transform" />
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">Facebook</span>
                   </a>
                   <a 
                     href={`https://api.whatsapp.com/send?text=${encodeURIComponent('🔥 ' + sharingPost.title + '\n\n' + sharingPost.content.substring(0, 100) + '...\n\nConfira no PKX: ' + window.location.origin + '/news/' + sharingPost.id)}`} 
                     target="_blank" rel="noreferrer"
                     className="flex flex-col items-center gap-3 p-6 bg-green-600/10 border border-green-600/20 rounded-3xl hover:bg-green-600/20 transition-all group"
                   >
                      <MessageCircle size={32} className="text-green-500 group-hover:scale-110 transition-transform" />
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">WhatsApp</span>
                   </a>
                   <a 
                     href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.origin + '/news/' + sharingPost.id)}&text=${encodeURIComponent('🔥 ' + sharingPost.title + ' | PKX Platform\n\n')}`} 
                     target="_blank" rel="noreferrer"
                     className="flex flex-col items-center gap-3 p-6 bg-white/5 border border-white/10 rounded-3xl hover:bg-white/10 transition-all group"
                   >
                      <Twitter size={32} className="text-white/60 group-hover:scale-110 transition-transform" />
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">Twitter (X)</span>
                   </a>
                   <button 
                     onClick={() => copyToClipboard(window.location.origin + '/news/' + sharingPost.id)}
                     className="flex flex-col items-center gap-3 p-6 bg-purple-600/10 border border-purple-600/20 rounded-3xl hover:bg-purple-600/20 transition-all group"
                   >
                      <LinkIcon size={32} className="text-purple-500 group-hover:scale-110 transition-transform" />
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">Copiar Link</span>
                   </button>
                </div>

                <div className="p-4 bg-black/40 border border-white/5 rounded-2xl flex items-center justify-between">
                   <span className="text-[10px] font-black text-white/20 truncate mr-4">{window.location.origin}/news/{sharingPost.id}</span>
                   <button onClick={() => copyToClipboard(window.location.origin + '/news/' + sharingPost.id)} className="text-[9px] font-black text-blue-500 uppercase tracking-widest hover:underline">Copiar</button>
                </div>
             </div>
          </div>
        )}

        {/* Gallery Modal */}
        {showGallery && (
          <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[300] flex items-center justify-center p-8 animate-in fade-in duration-300">
            <div className="bg-[#0c0d12] border border-white/10 rounded-[50px] w-full max-w-4xl p-12 space-y-8 flex flex-col max-h-[85vh] shadow-[0_0_150px_rgba(0,0,0,1)]">
              <div className="flex items-center justify-between">
                <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter">Minha Galeria <span className="text-white/10">(Nuvem ImgBB)</span></h2>
                <button onClick={() => setShowGallery(false)} className="p-4 bg-white/5 text-white rounded-full hover:bg-red-500 transition-all shadow-xl"><X size={24} /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 overflow-y-auto pr-6 custom-scrollbar">
                {(galleryImages || []).map((imgObj, i) => {
                  const url = imgObj.url || imgObj;
                  return (
                    <div key={i} className="aspect-square rounded-[35px] overflow-hidden border border-white/10 cursor-pointer group relative shadow-lg" onClick={() => { setPostData({...postData, image_url: url}); setShowGallery(false); }}>
                      <img alt="" src={url} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-blue-600/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all backdrop-blur-[2px]">
                        <span className="text-[11px] font-black text-white uppercase tracking-[4px]">Usar Foto</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}


        <div className="space-y-12">
          {(!news || news.length === 0) ? (
            <div className="pkx-card py-16 text-center text-[var(--muted)] font-bold">Nenhuma novidade publicada ainda. Volte em breve!</div>
          ) : (news || []).map((n) => {
            if (!n) return null;
            const catStyles = getCategoryStyles(n.category);
            return (
              <article key={n.id} className="bg-[#0c0d12]/60 backdrop-blur-md border border-white/5 rounded-3xl overflow-hidden shadow-2xl group animate-in zoom-in-95 duration-500 hover:border-[var(--color-main)]/30 transition-all flex flex-col">
                 
                 {/* IMAGEM COMPACTA - CLICÁVEL */}
                 {n.image_url && (
                   <div 
                     className="w-full relative aspect-[21/7] overflow-hidden z-10 cursor-pointer"
                     onClick={() => setSelectedImage(n.image_url)}
                   >
                     <img src={n.image_url} alt={n.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                     <div className="absolute inset-0 bg-gradient-to-t from-[#0c0d12] via-transparent to-transparent opacity-40" />
                     <div className="absolute top-4 left-4">
                        <div className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-[2px] backdrop-blur-xl border flex items-center gap-2 shadow-lg ${catStyles.bg} ${catStyles.text} ${catStyles.border} ${catStyles.glow}`}>
                          {catStyles.icon}
                          {n.category}
                        </div>
                     </div>
                   </div>
                 )}

                 {/* CONTEÚDO REFINADO */}
                 <div className="p-8 md:p-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-white/5 pb-6">
                      <div className="space-y-2 flex-1">
                        <h3 className="text-4xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none transition-all group-hover:text-[var(--color-main)]">{n.title}</h3>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                             <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${catStyles.bg} ${catStyles.border} ${catStyles.glow}`}>
                               {n.category?.toLowerCase() === 'torneio' ? <Trophy size={14} className={catStyles.text} /> : <Crown size={14} className={catStyles.text} />}
                             </div>
                             <p className="text-xs font-black text-white uppercase tracking-widest">{n.author || "ADMIN"}</p>
                          </div>
                          <div className="h-3 w-[1px] bg-white/10" />
                          <p className="text-xs text-white/40 font-bold uppercase tracking-wider">{new Date(n.created_at).toLocaleDateString()} — {new Date(n.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                        </div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => handleDeleteNews(n.id)} className="p-4 bg-red-500/5 text-red-500/20 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all self-start"><Trash2 size={20} /></button>
                      )}
                    </div>

                    <div className="text-white/60 leading-relaxed text-lg font-medium whitespace-pre-wrap">
                      {n.content}
                    </div>

                    <div className="flex items-center justify-between pt-6 border-t border-white/5">
                      <div className="flex items-center gap-8">
                        <button onClick={() => handleLike(n.id)} className="flex items-center gap-2 group/like transition-all active:scale-90">
                          <Heart 
                            size={24} 
                            className={`transition-all ${isRed ? 'group-hover/like:text-red-500' : 'group-hover/like:text-blue-500'} ${n.user_liked ? (isRed ? 'text-red-500 fill-red-500' : 'text-blue-500 fill-blue-500') : 'text-white/10'}`} 
                          />
                          <span className={`text-lg font-black transition-all ${n.user_liked ? 'text-white' : 'text-white/20'}`}>{n.likes || 0}</span>
                        </button>
                        <button onClick={() => handleShare(n)} className="text-white/10 hover:text-[var(--color-main)] transition-all hover:scale-110"><Share2 size={24} /></button>
                      </div>
                      
                      <div className="flex items-center gap-3 text-[9px] font-black text-white/10 uppercase tracking-[4px] italic">
                        PKX CONTENT <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      </div>
                    </div>
                 </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
